import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import { getRoleDisplayNameLocalized, getUserInitial } from '../../utils/userDisplay';
import ProfileAvatar from '../shared/ProfileAvatar';

const ProfileDropdown = ({ shellConfig = null }) => {
  const { isDark } = useTheme();
  const { user, logout, hasPermission } = useAdminAuth();
  const { t } = useTranslation('admin');
  const effectiveUser = shellConfig?.user || user;
  const effectiveLogout = shellConfig?.logout || logout;
  const effectiveHasPermission = shellConfig?.hasPermission || hasPermission;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const routes = useMemo(() => ({
    profile: shellConfig?.profilePath || '/admin/profile',
    settings: shellConfig?.settingsPath || '/admin/settings',
    pricing: shellConfig?.pricingPath || '/admin/credits',
    contact: shellConfig?.contactPath || '/admin/contact',
    logoutRedirect: shellConfig?.logoutRedirectPath || '/?auth=1',
  }), [shellConfig]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedAnchor = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!clickedAnchor && !clickedMenu) {
        setIsOpen(false);
      }
    };

    // Close dropdown on escape key
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleProfileClick = () => {
    navigate(routes.profile);
    setIsOpen(false);
  };

  const handleSettingsClick = () => {
    navigate(routes.settings);
    setIsOpen(false);
  };

  const handlePricingClick = () => {
    navigate(routes.pricing);
    setIsOpen(false);
  };

  const handleContactSupportClick = () => {
    navigate(routes.contact);
    setIsOpen(false);
  };

  const handleLogoutClick = () => {
    effectiveLogout();
    navigate(routes.logoutRedirect);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Get user display name
  const getUserDisplayName = () => {
    return effectiveUser?.name || effectiveUser?.email || t('admin.user_fallback');
  };

  // Get role display name
  const getRoleDisplayName = () => {
    return getRoleDisplayNameLocalized(effectiveUser?.role, t);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-2 p-1.5 rounded-lg transition-colors duration-200 hover:bg-white/10 dark:hover:bg-white/10"
        aria-label={t('admin.profile_menu')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <ProfileAvatar
          name={getUserDisplayName() || getUserInitial(effectiveUser)}
          avatarUrl={effectiveUser?.avatar_url}
          size="sm"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div 
            ref={menuRef}
            className={`fixed w-64 rounded-2xl border shadow-xl z-[9999] backdrop-blur-xl ${
              isDark 
                ? 'bg-zinc-900/80 border-white/10' 
                : 'bg-white/85 border-white/30'
            }`}
            style={{
              top: '100px', // Adjust based on topbar height
              right: '16px' // Position from right edge
            }}
          >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <ProfileAvatar
                name={getUserDisplayName() || getUserInitial(effectiveUser)}
                avatarUrl={effectiveUser?.avatar_url}
                size="md"
                previewable
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${
                  isDark ? 'text-white' : 'text-zinc-900'
                }`}>
                  {getUserDisplayName()}
                </p>
                <p className={`text-xs truncate ${
                  isDark ? 'text-zinc-400' : 'text-zinc-500'
                }`}>
                  {getRoleDisplayName()}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleProfileClick}
              className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                isDark 
                  ? 'text-zinc-300 hover:bg-white/10 hover:text-white' 
                  : 'text-zinc-700 hover:bg-white/60 hover:text-zinc-900'
              }`}
            >
              <div className="flex items-center space-x-3">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{t('admin.profile')}</span>
              </div>
            </button>

            {effectiveHasPermission('settings') && (
              <button
                onClick={handleSettingsClick}
                className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                  isDark 
                    ? 'text-zinc-300 hover:bg-white/10 hover:text-white' 
                    : 'text-zinc-700 hover:bg-white/60 hover:text-zinc-900'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{t('admin.settings')}</span>
                </div>
              </button>
            )}

            <button
              onClick={handlePricingClick}
              className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                isDark 
                  ? 'text-zinc-300 hover:bg-white/10 hover:text-white' 
                  : 'text-zinc-700 hover:bg-white/60 hover:text-zinc-900'
              }`}
            >
              <div className="flex items-center space-x-3">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('admin.pricing')}</span>
              </div>
            </button>

            <button
              onClick={handleContactSupportClick}
              className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                isDark 
                  ? 'text-zinc-300 hover:bg-white/10 hover:text-white' 
                  : 'text-zinc-700 hover:bg-white/60 hover:text-zinc-900'
              }`}
            >
              <div className="flex items-center space-x-3">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8-1.086 0-2.125-.172-3.09-.488L3 20l1.488-5.91C4.172 13.125 4 12.086 4 11c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>{t('admin.contact_support')}</span>
              </div>
            </button>

            <div className="border-t border-white/10"></div>

            <button
              onClick={handleLogoutClick}
              className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                isDark 
                  ? 'text-zinc-300 hover:bg-white/10 hover:text-white' 
                  : 'text-zinc-700 hover:bg-white/60 hover:text-zinc-900'
              }`}
            >
              <div className="flex items-center space-x-3">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t('admin.logout')}</span>
              </div>
            </button>
          </div>
        </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ProfileDropdown;
